import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, BookOpen, Gift, Flame, Shield, Zap } from 'lucide-react';
import { trackEvent } from './MetaPixel';
type PlanOption = 'base' | 'vip';
const CHECKOUT_URLS: Record<PlanOption, string> = {
  base: 'https://biologiafemenina.com.ar/checkouts/cn/hWNAjrQEer1ZqjO3pIx6oNkI/en-ar?_r=AQABmeHojiYcsqTSxNwx4O963pUsGxbMf4Z9o5NvUShqvM4',
  vip: 'https://biologiafemenina.com.ar/checkouts/cn/hWNAjrRowgn9aKIU6RteKHnW/en-ar?_r=AQABttqJLtggN2FI8o2Rov5zfQgghG6OCw9qELKd2aHzu5c'
};
const CTA_TEXT: Record<PlanOption, string> = {
  base: 'Comprar Protocolo Esencial — $19.990',
  vip: 'Comprar MegaKit S.O.S — $29.990'
};
const BASE_BENEFITS = [{
  icon: BookOpen,
  text: '6 tomos del Protocolo · Acceso de por vida'
}, {
  icon: Gift,
  text: '3 Bonus de regalo incluidos'
}, {
  icon: Shield,
  text: 'Garantía 7 días · Descarga inmediata'
}];
const VIP_EXTRAS = [{
  icon: Flame,
  text: 'Guía SOS Evacuación: protocolo paso a paso para aliviar puntadas, hinchazón y dolor intestinal en 30 minutos. Ideal para esos días donde ir al baño se vuelve una pesadilla.'
}, {
  icon: Zap,
  text: 'Escudo de Energía: plan de recuperación para eliminar la niebla mental, el cansancio extremo y el agotamiento que queda después de cada crisis de dolor.'
}];
export function PricingSelection() {
  const [selected, setSelected] = useState<PlanOption>('vip');
  return <section className="w-full bg-marfil pt-4 pb-24 px-4 flex justify-center" data-id="element-98">
      <div className="w-full max-w-md flex flex-col gap-2.5" data-id="element-99">
        <p className="font-serif font-bold text-petroleo text-xl leading-tight text-center" data-id="element-100">
          Elegí tu plan y empezá hoy
        </p>

        {/* Option A — Base */}
        <div onClick={() => setSelected('base')} className={`rounded-xl border-2 px-4 py-3 cursor-pointer transition-all duration-200 flex items-center justify-between ${selected === 'base' ? 'border-salvia bg-white shadow-sm' : 'border-gray-200 bg-white/60'}`} data-id="element-101">
          <div className="flex items-center gap-2.5" data-id="element-102">
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected === 'base' ? 'bg-salvia border-salvia' : 'border-gray-300 bg-white'}`} data-id="element-103">
              {selected === 'base' && <Check className="w-3 h-3 text-white" data-id="element-104" />}
            </div>
            <div data-id="element-105">
              <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-106">
                Esencial
              </span>
              <h3 className="font-serif font-bold text-petroleo text-sm leading-tight" data-id="element-107">
                Protocolo Endo-Rescate
              </h3>
            </div>
          </div>
          <span className="font-sans font-black text-petroleo text-lg" data-id="element-108">
            $19.990
          </span>
        </div>

        {/* Option B — VIP */}
        <div onClick={() => setSelected('vip')} className={`rounded-xl border-2 px-4 py-3 cursor-pointer transition-all duration-200 flex items-center justify-between ${selected === 'vip' ? 'border-salvia bg-salvia-light shadow-md ring-1 ring-salvia/20' : 'border-salvia/30 bg-salvia-light/30'}`} data-id="element-109">
          <div className="flex items-center gap-2.5" data-id="element-110">
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected === 'vip' ? 'bg-salvia border-salvia' : 'border-salvia/40 bg-white'}`} data-id="element-111">
              {selected === 'vip' && <Check className="w-3 h-3 text-white" data-id="element-112" />}
            </div>
            <div data-id="element-113">
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-white bg-salvia px-2 py-0.5 rounded-full" data-id="element-114">
                <Zap className="w-2.5 h-2.5" data-id="element-115" /> Más elegido
              </span>
              <h3 className="font-serif font-bold text-petroleo text-sm leading-tight" data-id="element-116">
                MegaKit S.O.S + Escudo de Energia
              </h3>
            </div>
          </div>
          <div className="flex flex-col items-end" data-id="element-117">
            <span className="text-gray-400 line-through text-[9px]" data-id="element-118">
              $47.900
            </span>
            <span className="font-sans font-black text-petroleo text-lg" data-id="element-119">
              $29.990
            </span>
          </div>
        </div>

        {/* CTA Button */}
        <motion.a key={selected} href={CHECKOUT_URLS[selected]} onClick={() => trackEvent('InitiateCheckout', {
        content_name: selected === 'vip' ? 'MegaKit S.O.S + Escudo de Energia' : 'Protocolo Esencial',
        value: selected === 'vip' ? 29990 : 19990,
        currency: 'ARS'
      })} initial={{
        scale: 0.95,
        opacity: 0.8
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 15
      }} className={`flex items-center justify-center w-full h-12 font-bold rounded-xl px-4 shadow-lg text-sm ${selected === 'vip' ? 'bg-salvia text-white' : 'bg-petroleo text-white'}`} data-id="element-120">
          {CTA_TEXT[selected]}
        </motion.a>

        {/* Trust badges */}
        <p className="text-center text-gray-400 text-[10px]" data-id="element-121">
          🔒 Mercado Pago · 📲 Descarga Instantánea · ↩️ Garantía 7 días
        </p>

        {/* Value Description */}
        <div className="bg-white rounded-xl p-4 border border-salvia-light/50" data-id="element-122">
          <p className="font-serif font-bold text-petroleo text-sm mb-2" data-id="element-123">
            ¿Qué incluye mi selección?
          </p>
          <ul className="flex flex-col gap-1.5" data-id="element-124">
            {BASE_BENEFITS.map((item, i) => <li key={i} className="flex items-center gap-2 text-[12px] text-gray-600" data-id="element-125">
                <item.icon className="w-3.5 h-3.5 text-salvia flex-shrink-0" data-id="element-126" />
                <span data-id="element-127">{item.text}</span>
              </li>)}
            <AnimatePresence data-id="element-128">
              {selected === 'vip' && VIP_EXTRAS.map((item, i) => <motion.li key={`vip-${i}`} initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.15,
              delay: i * 0.05
            }} className="flex items-center gap-2 text-[12px] font-semibold text-petroleo overflow-hidden" data-id="element-129">
                    <div className="bg-salvia rounded-full p-0.5 flex-shrink-0" data-id="element-130">
                      <Check className="w-2.5 h-2.5 text-white" data-id="element-131" />
                    </div>
                    <span data-id="element-132">{item.text}</span>
                  </motion.li>)}
            </AnimatePresence>
          </ul>
          <p className="text-gray-500 text-[10px] italic mt-2.5 text-center" data-id="element-133">
            Menos que una consulta médica, con resultados desde el día 1.
          </p>
        </div>
      </div>
    </section>;
}